import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingSuccess = ({ lang, t }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const orderId = searchParams.get('order_id');

  const checkPayment = useCallback(async () => {
    if (!orderId) {
      setPaymentStatus('success');
      return;
    }

    try {
      const maxAttempts = 5;
      for (let i = 0; i < maxAttempts; i++) {
        const response = await fetch(`${API_URL}/api/revolut-status/${orderId}`);
        const data = await response.json();

        if (data.payment_status === 'paid' || data.state === 'completed') {
          setPaymentStatus('success');
          return;
        } else if (data.state === 'cancelled' || data.state === 'failed') {
          setPaymentStatus('error');
          return;
        }

        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // After retries, assume success since the widget already confirmed
      setPaymentStatus('success');
    } catch (err) {
      console.error('Payment check error:', err);
      // Widget already confirmed payment, so show success
      setPaymentStatus('success');
    }
  }, [orderId]);

  useEffect(() => {
    checkPayment();
  }, [checkPayment]);

  const generateWhatsAppLink = useCallback(() => {
    const waNumber = process.env.REACT_APP_WHATSAPP_NUMBER;
    const message = `Bonjour, ma réservation a été confirmée. Merci !`;
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
  }, []);

  const handleBackHome = useCallback(() => navigate('/'), [navigate]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center" data-testid="booking-success">
        {paymentStatus === 'checking' && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 text-[#C5A059] animate-spin mx-auto" />
            <p className="text-white/70">
              {lang === 'fr' ? 'Vérification du paiement...' : 'Checking payment...'}
            </p>
          </div>
        )}

        {paymentStatus === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#C5A059]" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-light text-white mb-4 tracking-tighter" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('success.title')}
            </h1>
            <p className="text-white/70 mb-8">
              {t('success.message')}
            </p>

            <div className="space-y-4">
              <a
                href={generateWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp w-full"
                data-testid="success-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                {t('success.whatsappMessage')}
              </a>

              <button
                onClick={handleBackHome}
                className="btn-secondary w-full flex items-center justify-center gap-2"
                data-testid="back-home-btn"
              >
                <ArrowLeft size={18} />
                {t('success.backHome')}
              </button>
            </div>
          </>
        )}

        {paymentStatus === 'error' && (
          <>
            <h1 className="text-2xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {lang === 'fr' ? 'Erreur' : 'Error'}
            </h1>
            <p className="text-white/70 mb-8">
              {lang === 'fr' ? 'Une erreur s\'est produite. Veuillez réessayer.' : 'An error occurred. Please try again.'}
            </p>
            <button
              onClick={handleBackHome}
              className="btn-primary"
              data-testid="retry-btn"
            >
              {t('success.backHome')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingSuccess;
